import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, ArrowLeft, Calendar } from 'lucide-react';
import { HomlyLogo } from '../../components/brand/HomlyLogo';
import { BLOG_ARTICLES } from './blogData';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function ArticleCard({ article, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/blog/${article.slug}`}
        data-testid={`blog-card-${article.slug}`}
        className="group block rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-8 transition-all duration-500 hover:border-white/[0.12] hover:bg-white/[0.04]"
      >
        {/* Category + Read time */}
        <div className="flex items-center gap-3 mb-4">
          <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r ${article.heroColor} text-white`}>
            {article.category}
          </span>
          <span className="text-xs text-white/30 flex items-center gap-1">
            <Clock className="w-3 h-3" />{article.readTime}
          </span>
        </div>

        {/* Title */}
        <h2 className="text-lg sm:text-xl font-bold text-white mb-3 tracking-tight group-hover:text-white/90 transition-colors leading-tight">
          {article.title}
        </h2>

        {/* Excerpt */}
        <p className="text-sm text-white/45 leading-relaxed mb-5 line-clamp-3">
          {article.excerpt}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/25 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />{formatDate(article.date)}
          </span>
          <span className="text-sm font-medium text-white/50 flex items-center gap-1.5 group-hover:text-white/70 transition-colors">
            Lire <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function BlogPage() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.title = 'Blog Homly — Conseils pour agences immobilières';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Conseils, stratégies et bonnes pratiques pour les directeurs d\'agences immobilières. Suivi mandat, KPIs, fidélisation vendeur et réputation agent.');
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-[#0a0a1a]/95 backdrop-blur-md border-b border-white/[0.06]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <HomlyLogo size="md" />
            <span className="text-xs font-medium text-white/30 border-l border-white/10 pl-3">Blog</span>
          </Link>
          <Link
            to="/agences"
            data-testid="blog-back-landing"
            className="text-sm text-white/50 hover:text-white transition-colors flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Retour
          </Link>
        </div>
      </nav>

      {/* Header */}
      <header className="pt-16 sm:pt-20 pb-10 sm:pb-14 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/30 mb-3">Le blog Homly</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
              Ressources pour{' '}
              <span className="bg-gradient-to-r from-[#681793] via-[#9b4dca] to-[#FF914D] bg-clip-text text-transparent">
                directeurs d'agence
              </span>
            </h1>
            <p className="text-base sm:text-lg text-white/40 max-w-2xl">
              Stratégies, KPIs et bonnes pratiques pour piloter votre agence, fidéliser vos vendeurs et développer votre activité.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Articles grid */}
      <section className="px-4 pb-20">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {BLOG_ARTICLES.map((article, i) => (
            <ArticleCard key={article.slug} article={article} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 sm:p-12">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">Mettez ces conseils en pratique</h2>
            <p className="text-sm text-white/40 mb-6 max-w-md mx-auto">
              Homly vous donne les outils pour piloter vos mandats, suivre vos KPIs et fidéliser vos vendeurs.
            </p>
            <Link
              to="/agences"
              data-testid="blog-cta-demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Demander une démo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
